import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Menu, X, User, Search } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const { isAuthenticated, user, logout } = useAuth();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Products', href: '/products' },
    { name: 'Event Catering', href: '/event-catering' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            {/* Hookah Crest Logo with 3D Effect */}
            <div className="relative group">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#FFD700] to-[#B8860B] blur-md opacity-50 group-hover:opacity-75 transition-opacity"></div>
              <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] via-[#FFD700] to-[#C5A028] flex items-center justify-center shadow-2xl border-2 border-[#FFD700]/30">
                <div className="absolute inset-1 rounded-full bg-gradient-to-br from-white/20 to-transparent"></div>
                <svg
                  viewBox="0 0 64 64"
                  fill="none"
                  className="w-9 h-9 relative z-10"
                >
                  {/* Hookah Base */}
                  <path
                    d="M32 48C28 48 25 45 25 42L25 38L39 38L39 42C39 45 36 48 32 48Z"
                    fill="#0a1128"
                    opacity="0.9"
                  />
                  {/* Water in base */}
                  <ellipse cx="32" cy="38" rx="7" ry="2" fill="#4A90E2" opacity="0.6"/>
                  
                  {/* Hookah Stem */}
                  <rect x="30" y="25" width="4" height="13" fill="#0a1128" opacity="0.9"/>
                  
                  {/* Bowl */}
                  <path
                    d="M27 20C27 18 29 16 32 16C35 16 37 18 37 20L37 25L27 25L27 20Z"
                    fill="#0a1128"
                    opacity="0.9"
                  />
                  
                  {/* Hot Coals */}
                  <circle cx="29" cy="18" r="1.5" fill="#FF6B35"/>
                  <circle cx="32" cy="17" r="1.5" fill="#FF8C42"/>
                  <circle cx="35" cy="18" r="1.5" fill="#FF6B35"/>
                  
                  {/* Smoke/Flame effect - wavy abstract */}
                  <path
                    d="M32 15 Q30 12 31 9 Q32 11 32 8 Q32 11 33 9 Q34 12 32 15"
                    stroke="#D4AF37"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.7"
                  />
                  <path
                    d="M28 14 Q27 11 28 9"
                    stroke="#FFD700"
                    strokeWidth="0.8"
                    fill="none"
                    opacity="0.6"
                  />
                  <path
                    d="M36 14 Q37 11 36 9"
                    stroke="#FFD700"
                    strokeWidth="0.8"
                    fill="none"
                    opacity="0.6"
                  />
                  
                  {/* Hoses */}
                  <path
                    d="M25 35 Q20 35 18 37"
                    stroke="#0a1128"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.8"
                  />
                  <path
                    d="M39 35 Q44 35 46 37"
                    stroke="#0a1128"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.8"
                  />
                </svg>
                {/* Bottom shine effect */}
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-8 h-2 bg-white/40 rounded-full blur-sm"></div>
              </div>
            </div>
            <div className="text-xl font-display font-bold text-[#0a1128] tracking-tight">
              Smithhookahsupplyco
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-sm font-medium text-gray-700 transition-colors hover:text-[#D4AF37]"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" className="hidden md:flex">
              <Search className="h-5 w-5" />
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/account">My Account</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders">Orders</Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin">Admin Panel</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout}>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="icon" asChild>
                <Link to="/login">
                  <User className="h-5 w-5" />
                </Link>
              </Button>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setIsCartOpen(true)}
              data-testid="inquiry-btn"
            >
              <MessageSquare className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#D4AF37] hover:bg-gray-50 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;